'use client'

/**
 * The mount that closes the feedback-pin seam (Issue #13639, epic #13573).
 *
 * ## What this file is, and why it is a file
 *
 * `PinOverlay` (#13578) takes its anchors as a `loadAnchors` PROP because F1
 * (#13574) was unmerged when it shipped, and an import of a fetch that did
 * not exist would not have typechecked. F1 then shipped
 * `listFeedbackAnchorsForRoute` / `listAllFeedbackAnchorsForRoute` with no
 * caller. Both halves landed the same day, every per-child acceptance
 * criterion passed, and no pin ever rendered in the app.
 *
 * This module is that missing caller and nothing else. It adds no behaviour
 * of its own: the visibility rule is the server's, the positioning is the
 * overlay's, the popover is the popover's. What it decides is exactly two
 * things — with WHAT route to read, and HOW OFTEN.
 *
 * ## It does NOT decide WHICH action to call, and that is a fix
 *
 * It used to. It picked `listAllFeedbackAnchorsForRoute` for an admin and
 * `listFeedbackAnchorsForRoute` otherwise, from `session.user.orgRole` — the
 * REAL signed-in user's role, which nothing rewrites during impersonation
 * (`src/lib/auth/auth.ts:427`). The admin action gates on the OPPOSITE
 * identity, because `adminAction` → `withRole` resolves the effective person
 * via the impersonation-aware `getCurrentUserId()`
 * (`src/lib/middleware/with-role.ts:83`). An admin impersonating a member
 * therefore called the admin action, was refused as that member, and saw no
 * pins whatsoever — plus one `FEEDBACK_PIN_ANCHORS_LOAD_FAILED` per
 * navigation for the life of the impersonation session. The same failure
 * #13639 exists to fix, reached from the other side.
 *
 * The branch was also unnecessary. `listFeedbackAnchorsForRoute` re-derives
 * the role server-side from `viewingAsUserId ?? actorId` and returns the
 * org-wide set when that identity is an admin;
 * `listAllFeedbackAnchorsForRoute`'s own header says it is "NOT a wider read
 * than the action above". So the branch bought a refusal and nothing else,
 * and removing it makes the impersonation skew unrepresentable rather than
 * merely patched.
 *
 * ## The loader is `useCallback`-stable, and that is load bearing
 *
 * `PinOverlay`'s fetch effect keys on `[loadAnchors, route]` and CLEARS the
 * pins before refetching (`pin-overlay.tsx:172`), which is what makes "route
 * change shows no stale pins" true. The same mechanism turns an unstable
 * loader into a defect: an inline arrow gets a fresh identity on every parent
 * render, so every unrelated re-render of the root layout — a session poll, a
 * theme change, a navigation — would blank the overlay and repaint it. The
 * prop's own doc says so in as many words.
 *
 * Its dependencies are therefore the only things that SHOULD force a refetch:
 * whether the viewer can read at all, and the refresh counter a triage bumps.
 *
 * ## Routes with no readable session
 *
 * The root layout wraps `/auth/signin` too, and an authenticated session can
 * still carry `orgId: undefined` (`buildPartialSessionForMissingPerson`, and
 * the org-resolution `catch` at `src/lib/auth/auth.ts:440`). The action is an
 * authenticated, org-scoped read whose `withOrgScoping` throws in either
 * state, so firing it there logs a failure on every navigation for as long as
 * the condition lasts. `canRead` gates both cases and the loader resolves
 * empty instead. This is not a security boundary — the action enforces that
 * itself — it is about not manufacturing errors on a page that is working
 * correctly.
 *
 * ## Pins are part of feedback mode now (#13756)
 *
 * This mount used to draw on every route for every viewer who could see a
 * pin, and it refetched only when an admin triaged. Both halves were defects:
 * a page permanently dotted with other people's pins is noise for everyone who
 * is not currently giving feedback, and a viewer who submitted anchored
 * feedback saw nothing change, because the submit raised no signal the overlay
 * listens for.
 *
 * So two of this file's decisions are now read from `useFeedbackMode()`:
 *
 *  - WHETHER TO READ AT ALL was gated on the mode being on. #13788 REPLACED
 *    that gate rather than removing it — see below.
 *  - WHETHER TO DRAW is `pinsVisible`, the context's single derivation of
 *    "the flag is on, a mode is live, the picker is not mid-selection". It is
 *    deliberately not the same gate as the read: the mid-selection suppression
 *    is a paint, and refetching on it would blank and repaint the overlay
 *    every time the picker stopped previewing an element.
 *
 * ## Pins FOLLOW THE MODE (#13841), and the read follows them
 *
 * #13756 coupled pins to the mode; #13788 decoupled them, making the layer
 * ambient on every page and paying for the now-ambient anchor read with a
 * viewer show/hide preference; #13841 couples them again and deletes the
 * preference with the ambience that made it necessary.
 *
 * The property #13788 was really protecting — that replying to a pin must not
 * require standing under a full-viewport click interceptor — is NOT given back
 * up. It is carried by the three-state machine that same issue introduced:
 * `browsing` is a mode with the capture layer down, so a viewer can read and
 * answer a thread inside the mode. What goes away is pins on pages nobody
 * asked to see them on, and with them the per-navigation read for a viewer who
 * never opened the feature at all.
 *
 * The refresh counter moved into the context for the same reason: the feedback
 * modal that needs to bump it is not a child of this mount. There is still
 * exactly ONE counter — `onTriaged` now calls the same `requestAnchorRefresh`
 * the submit does, because two counters are two things that can disagree about
 * whether the overlay is holding fresh data.
 *
 * ## Hidden threads: counted AND listed (#13653)
 *
 * The overlay reports the pins on the route whose element is not laid out —
 * a thread inside a collapsed section, a closed tab panel — through
 * `onHiddenCountChange`. #13639 deliberately left it unwired, because
 * surfacing it needed shared state and a new affordance; until #13653 such a
 * thread rendered nowhere and was counted nowhere, so from the viewer's side
 * the feedback did not exist on the page it was filed against.
 *
 * THE DECISION (#13653 acceptance criterion 1): shape 2, in its smallest form
 * that leaves the viewer a next action. Of the two shapes the issue offered:
 *
 *  1. A bare count badge on the toggle — REJECTED. "3 hidden" without saying
 *     where gives the viewer nothing to do with the number.
 *  2. An affordance that reveals them — SHIPPED, as a list rather than as
 *     auto-expanding the owning section. The badge on the banner's hidden-
 *     threads control carries the count; activating it opens the SAME panel
 *     a pin opens (`FeedbackPinPopover`), over the hidden pins, so each
 *     thread can be read and answered directly — its thread switcher is the
 *     list. Expanding sections is not attempted: there is no generic way to
 *     find and open "the section that owns this element" across the app's
 *     accordions, tabs and disclosure widgets, and guessing wrong mutates a
 *     page the viewer is only annotating.
 *
 * "The feedback-mode toggle" is the BANNER (`hidden-feedback-threads.tsx`,
 * rendered by `feedback-mode-banner.tsx`). #13715 deleted the header toggle,
 * and the banner is the mode's only control surface — mounted on every route,
 * headerless ones included, which is also the only place pins can be seen,
 * since pins follow the mode (#13841).
 *
 * This mount is the publisher: it forwards the report into
 * `feedback-mode-context` with the two inputs the panel needs that only this
 * file resolves — `isAdmin` (impersonation-aware) and the server-resolved
 * `timezone`. The overlay keeps the pin array identity-stable while the
 * hidden set is unchanged, so a scroll does not re-render the banner.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { trackFeedbackModeEvent } from '@/app/actions/feedback-mode-analytics'

import { PinOverlay } from '@/components/feedback/pin-overlay'
import { useFeedbackMode } from '@/components/feedback/feedback-mode-context'
import { stripInlineMarkdown } from '@/components/markdown/strip-inline-markdown'
import { useImpersonation } from '@/components/impersonation-context'
import { SHELL_ENDPOINTS, shellReadResult } from '@/lib/shell-transport'
import type { FeedbackAnchorListItem } from '@/lib/feedback/anchor-list'
import type { FeedbackPinAnchor } from '@/lib/feedback/pin-anchor'
import type { PositionedPin } from '@/components/feedback/pin-cluster'
import { isAdminRole } from '@/lib/role-utils'

export interface PinOverlayMountProps {
  /**
   * The viewer's DISPLAY zone, resolved by the server and passed in.
   *
   * Required, and deliberately not defaulted: ADR-0101 Decision 1 fixes the
   * chain at `user.timezone → organization.timezone → DEFAULT_ORG_TIMEZONE`
   * and `src/lib/display-timezone.ts` states it is the sole sanctioned
   * resolver, "with no exceptions" and "do not add a second way". This
   * component previously called `detectBrowserTimezone()`, which is a second
   * way and skips the first two legs — a viewer who pinned a zone in
   * `/settings/profile` saw feedback timestamps in a different zone from
   * every other surface. A default here would let that failure back in
   * silently, so the only way to render this component is to have resolved
   * the zone properly. `PinOverlayMountSlot` is the one caller that does.
   */
  timezone: string
}

/** No fallback position was captured. See {@link toPinOverlayAnchor}. */
const NO_PIN_RECT = { top: 0, left: 0, width: 0, height: 0 } as const

/**
 * One stored anchor row, as the overlay wants it.
 *
 * The two vocabularies differ in three ways and all three are silent
 * failures if got wrong, so they are spelled out here rather than inlined:
 *
 *  1. **The rect is renamed, not recomputed.** Storage writes
 *     `{ x, y, w, h, vw, vh }` (`feedbackAnchorPinRectSchema`); the overlay
 *     reads `{ top, left, width, height }` plus a separate viewport.
 *  2. **The status and the reply count live on the FEEDBACK, not the
 *     anchor.** The anchor row has neither. Reading them off the wrong object
 *     leaves every marker permanently `NEW` with no reply count — which still
 *     renders, and renders wrongly.
 *  3. **A null `pinRect` is a real state**, not a missing value.
 *     `toFeedbackAnchorPinRect` returns null when the capture produced
 *     nothing usable (an unlaid-out element, a rect off the viewport). There
 *     is no fallback position to draw, so it degrades to a ZERO rect: the
 *     overlay measures that as not-laid-out and counts the pin as hidden,
 *     instead of stacking a marker at the top-left of a page the element was
 *     never in. Inventing `{0,0,1,1}` would do the latter.
 *
 * ### The one lossy conversion, named
 *
 * `PinRect` is documented as DOCUMENT-relative and the overlay subtracts the
 * current scroll from it (`pin-overlay.tsx:251`, pinned by that file's
 * "coordinate space of the fallback pin" tests). What storage actually holds
 * is VIEWPORT-relative at capture time, and no capture-time scroll offset is
 * recorded anywhere — so the two spaces cannot be reconciled from the data
 * that exists. The stored values are passed through as-is, which places a
 * fallback pin as though the page had been at scroll 0 when it was captured.
 * That is an approximation, it applies ONLY on the degraded path where the
 * selector no longer resolves, and the pin still scrolls with the page rather
 * than sticking to the glass. Making it exact needs a capture-scroll column,
 * which is a schema change this issue is not scoped to.
 */
export function toPinOverlayAnchor(
  item: FeedbackAnchorListItem,
): FeedbackPinAnchor {
  const rect = item.pinRect
  // #13707: feedback prose renders as markdown now, and this preview becomes
  // the accessible name of a pin-picker button in `feedback-pin-popover.tsx`.
  // A name carrying the raw syntax is announced as "asterisk asterisk card" —
  // #12373's finding, and the reason `stripInlineMarkdown` exists. Stripped
  // BEFORE the emptiness check below so a preview that is nothing but syntax
  // strips to '' and is absent, rather than drawing an empty header row.
  const summary = stripInlineMarkdown(item.feedback.messagePreview ?? '')
  return {
    feedbackId: item.feedbackId,
    route: item.route,
    selectorPath: item.selectorPath,
    pinRect: rect
      ? { top: rect.y, left: rect.x, width: rect.w, height: rect.h }
      : { ...NO_PIN_RECT },
    capturedViewport: rect ? { width: rect.vw, height: rect.vh } : null,
    status: item.feedback.status,
    replyCount: item.feedback.commentCount,
    // Optional, and an EMPTY preview is absent rather than present-and-empty:
    // the popover renders a header row for a summary it has, so `''` would
    // draw an empty one.
    ...(summary ? { summary } : {}),
  }
}

/**
 * The query parameter a `/feedback` row's "See in context" navigates with
 * (#13788). Exported so the list builds the URL from the same spelling this
 * reads it with — the two used to be the classic pair of independent string
 * literals that document each other and then drift.
 */
export const FEEDBACK_DEEP_LINK_PARAM = 'feedback'

/**
 * Append the deep-link parameter to a stored anchor route, structurally.
 *
 * NOT `` `${route}?${PARAM}=${id}` `` (#13788). `isValidFeedbackAnchorRoute`
 * requires a leading `/` and `isSafeSubmissionUrl`, and neither rejects a query
 * string or a fragment — so a stored route of `/people?tab=all` would
 * concatenate to `/people?tab=all?feedback=<id>`, where the second `?` is an
 * ordinary character inside the `tab` value. `searchParams.get('feedback')`
 * then returns null, the landing opens nothing, and the failure is silent.
 *
 * Today the picker writes `usePathname()`, which carries no query string, so
 * this is latent rather than live — but the value is client-supplied and the
 * validator is what would have to guarantee otherwise, and it does not.
 * Any fragment is re-appended last, where it belongs.
 */
export function buildFeedbackDeepLink(
  route: string,
  feedbackId: string,
): string {
  const hashAt = route.indexOf('#')
  const hash = hashAt === -1 ? '' : route.slice(hashAt)
  const withoutHash = hashAt === -1 ? route : route.slice(0, hashAt)

  const queryAt = withoutHash.indexOf('?')
  const path = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt)
  const params = new URLSearchParams(
    queryAt === -1 ? '' : withoutHash.slice(queryAt + 1),
  )
  params.set(FEEDBACK_DEEP_LINK_PARAM, feedbackId)

  return `${path}?${params.toString()}${hash}`
}

export function PinOverlayMount({ timezone }: PinOverlayMountProps) {
  const route = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { data: session, status } = useSession()
  const { isImpersonating } = useImpersonation()
  /**
   * The org flag and the MODE gate the READ (#13841), `pinsVisible` gates the
   * DRAW, and `anchorRefreshToken` is the one counter both a triage and an
   * anchored submit advance. Reading them here rather than deriving anything
   * locally is what keeps the banner, the picker's suspension and this
   * overlay from holding three answers to one question.
   */
  const {
    isEnabled,
    isActive,
    pinsVisible,
    anchorRefreshToken,
    requestAnchorRefresh,
    setPinThreadOpen,
    setHiddenThreads,
    activateBrowsing,
    deactivate,
  } = useFeedbackMode()

  /**
   * ONLY the popover's triage affordances — never which action to call. See
   * `loadAnchors` below for why that branch no longer exists.
   *
   * `session.user.orgRole` is the REAL signed-in user's role: the Auth.js
   * session callback resolves it from `userRecord`'s memberships
   * (`src/lib/auth/auth.ts:427`) and nothing rewrites it during
   * impersonation. Every action behind these affordances gates on the
   * OPPOSITE identity — `triageFeedback` is `adminAction`, whose `withRole`
   * resolves the effective person through the impersonation-aware
   * `getCurrentUserId()` (`src/lib/middleware/with-role.ts:83`). So showing
   * them while impersonating offers controls that are guaranteed to refuse,
   * to a session `impersonation-context.tsx` itself calls `isReadOnly`.
   */
  const isAdmin = !isImpersonating && isAdminRole(session?.user?.orgRole)

  /**
   * An authenticated session can still carry `orgId: undefined` — both
   * `buildPartialSessionForMissingPerson` and the org-resolution `catch` in
   * `src/lib/auth/auth.ts:440` produce one. Both actions run
   * `withOrgScoping`, whose `getCurrentOrgId()` throws in that state, so
   * firing on a session without an org manufactures one
   * `FEEDBACK_PIN_ANCHORS_LOAD_FAILED` per navigation for as long as the
   * session lasts. Same standard as the signed-out guard in the header
   * above: do not generate errors on a page that is working correctly.
   */
  const orgId = session?.user?.orgId
  /**
   * #13841 — the READ gate is the MODE again.
   *
   * #13673 moved this read off the server-action queue and onto
   * `/api/shell/feedback-anchors` precisely because it was a navigation-losing
   * shell burst (#13434), and this component is in the root layout so the
   * loader runs on every route change. #13788 made the read ambient and paid
   * for it with a viewer preference; #13841 deletes the preference and gates
   * on `isActive` instead, which is strictly cheaper than either arrangement:
   * a viewer who is not in feedback mode issues no anchor request at all.
   *
   * `isActive`, not `mode === 'placing'`: `browsing` is the state a viewer
   * reads and replies in, so gating on `placing` would empty the layer at the
   * moment it is most wanted.
   *
   * `isActive` and NOT `pinsVisible`: the latter also carries the picker's
   * mid-selection suppression, which flips as the pointer crosses elements, so
   * gating the read on it would blank and refetch the overlay on every mouse
   * move.
   *
   * No hydration term is needed any more. The preference this replaces
   * defaulted to `true` during render and was corrected in an effect, so the
   * loader could fire once per navigation for a viewer who had opted out
   * before that effect landed. `mode` starts at `off` and is only ever
   * widened by a restore, so the pre-hydration answer here is the SAFE one.
   */
  const canRead =
    status === 'authenticated' && Boolean(orgId) && isEnabled && isActive

  /**
   * #13653 — publish the overlay's hidden pins for the banner to badge and
   * list. `isAdmin` and `timezone` are dependencies on purpose: a change to
   * either changes this callback's identity, the overlay's report effect
   * re-runs, and the context gets the fresh pair without a second channel.
   */
  const reportHiddenPins = useCallback(
    (count: number, pins: readonly PositionedPin[]) => {
      setHiddenThreads(count === 0 ? null : { pins, isAdmin, timezone })
    },
    [setHiddenThreads, isAdmin, timezone],
  )
  // Unmount withdraws the report: a list of threads from a layer that no
  // longer exists would open panels for pins nobody is drawing.
  useEffect(() => () => setHiddenThreads(null), [setHiddenThreads])

  const loadAnchors = useCallback(
    async (currentRoute: string): Promise<FeedbackPinAnchor[]> => {
      if (!canRead) return []

      // ONE read, unconditionally — the admin/member branch this used to
      // carry was not just redundant, it inverted the feature while an admin
      // was impersonating.
      //
      // `listFeedbackAnchorsForRoute` applies the epic's visibility rule
      // SERVER-side, re-deriving the role from the effective identity
      // (`viewingAsUserId ?? actorId`) and returning the org-wide set when
      // that identity is really an admin. `listAllFeedbackAnchorsForRoute`'s
      // own header says so: "NOT a wider read than the action above: that one
      // already returns everything to an admin caller."
      //
      // So the only thing the branch bought was a REFUSAL for a non-admin —
      // and it chose that branch from `session.user.orgRole`, the real user's
      // role, while the gate evaluates the impersonated one. An admin
      // impersonating a member therefore called the admin action, was refused
      // as that member, and saw NO pins at all plus one logged error per
      // navigation. Choosing the gate client-side cannot be made correct
      // here; not choosing it can.
      //
      // OVER HTTP, NOT AS A SERVER ACTION (#13673). This component is mounted
      // in the ROOT LAYOUT and this loader runs on every route change, so
      // calling the action directly put one dispatch into the App Router
      // action queue on every route mount — the shell burst that loses a
      // user's navigation, which `src/lib/shell-transport.ts` exists to keep
      // out of the shell and which
      // `navigation-survives-shell-burst-13434.spec.ts` caught. The same
      // action still runs, and still owns the visibility rule and the route
      // validation; only the transport changed.
      const transport = await shellReadResult<
        | { success: true; data: FeedbackAnchorListItem[] }
        | { success: false; error: string }
      >(SHELL_ENDPOINTS.feedbackAnchors, { route: currentRoute })

      // `shellReadResult`, not `shellRead`, so a transport failure stays a
      // failure: `null` would flatten into the same empty render as "this
      // page has no feedback" and the throw below would never happen.
      if (!transport.ok) {
        throw new Error(transport.error)
      }

      const result = transport.data

      if (!result.success) {
        // THROWN, not swallowed into `[]`. The overlay already catches and
        // logs this with the route attached
        // (`ERROR_IDS.FEEDBACK_PIN_ANCHORS_LOAD_FAILED`), deliberately
        // without a toast — feedback mode annotates someone else's page.
        // Returning an empty array instead would render identically to "this
        // page has no feedback", which is how a broken read stays invisible.
        throw new Error(result.error)
      }

      return result.data.map(toPinOverlayAnchor)
    },
    // `anchorRefreshToken` is the whole point of the dependency, not an
    // oversight: changing the loader's identity is what makes the overlay
    // refetch, and since #13756 the counter lives on the context so a submit
    // from the modal advances the same one a triage does. `canRead` carries
    // the mode again since #13841, so entering it refetches too. `isAdmin` is NOT a dependency —
    // the loader no longer reads it, and leaving it here would refetch on a
    // change that cannot affect the result. `orgId` is a string, so identity
    // stays stable across renders.
    [canRead, anchorRefreshToken],
  )

  /**
   * The `?feedback=<id>` landing (#13788).
   *
   * Three things happen once, in this order: the mode goes to `browsing`
   * (banner up, capture layer DOWN — the viewer arrived to read, not to
   * place), the id is held so the matching pin opens itself, and the
   * parameter is stripped from the URL.
   *
   * Stripping it matters. Left in place it is in every copied link and in the
   * browser's own back/forward entries, so a refresh — or a Back that returns
   * here after the viewer closed the panel — reopens a thread they closed.
   * `router.replace` rather than `push`, so the cleanup does not become its
   * own history entry.
   *
   * There is deliberately NO not-found path. Edge case 6: the anchors read
   * already filters out a thread this viewer cannot see, so the pin simply is
   * not there. An error saying "that feedback does not exist here" would
   * disclose whether it exists somewhere.
   */
  const deepLinkParam = searchParams?.get(FEEDBACK_DEEP_LINK_PARAM) ?? null
  const [deepLinkId, setDeepLinkId] = useState<string | null>(null)
  const consumedRef = useRef<string | null>(null)

  // True while the mode on screen is one THIS landing entered, so the route
  // change that retires the landing can put it back down. A deep link is a
  // read-only affordance; it must not leave a mode standing on every
  // subsequent page (#13788).
  const enteredForLandingRef = useRef(false)

  useEffect(() => {
    if (deepLinkParam === null || consumedRef.current === deepLinkParam) return
    consumedRef.current = deepLinkParam
    setDeepLinkId(deepLinkParam)
    enteredForLandingRef.current = true
    // `activateBrowsing`, not `activate()` followed by `beginBrowsing()`:
    // the second of that pair reads `isActive` from a closure the first has
    // not re-rendered yet, so it refuses and the viewer lands under the
    // capture layer they followed a "see in context" link to avoid.
    activateBrowsing()

    const params = new URLSearchParams(searchParams?.toString() ?? '')
    params.delete(FEEDBACK_DEEP_LINK_PARAM)
    const query = params.toString()
    router.replace(query ? `${route}?${query}` : route, { scroll: false })
  }, [deepLinkParam, activateBrowsing, router, route, searchParams])

  /**
   * A route change retires the landing: the pin it named is not on this page.
   *
   * Keyed off a REF of the previous route rather than firing on mount, because
   * effects run in declaration order within one commit — so a mount-time run
   * would clear the id the effect above had just set, and the deep link would
   * silently never open anything.
   */
  const lastRouteRef = useRef(route)
  useEffect(() => {
    if (lastRouteRef.current === route) return
    lastRouteRef.current = route
    // The consumption marker is retired with the landing it belongs to.
    // Without this the ref outlives its meaning: `PinOverlayMount` is in the
    // root layout, so it survives every client navigation, and a second
    // "See in context" on the SAME feedback id would match the stale value,
    // return on the effect's first line, and open nothing — leaving
    // `?feedback=<id>` in the address bar, because the `router.replace` that
    // strips it sits below that return.
    consumedRef.current = null
    setDeepLinkId(current => (current === null ? current : null))
    // The landing is over, so the mode it entered goes with it. Only the mode
    // THIS effect's landing put up: a viewer who was already `placing` or
    // `browsing` when they followed the link took the `mode !== 'off'` branch
    // in `activateBrowsing`, which never sets this flag, and their own mode is
    // theirs to leave.
    if (enteredForLandingRef.current) {
      enteredForLandingRef.current = false
      deactivate()
    }
  }, [route, deactivate])

  return (
    <PinOverlay
      route={route}
      loadAnchors={loadAnchors}
      isAdmin={isAdmin}
      timezone={timezone}
      // #13788 — the picker suspends for as long as a thread is open, so a
      // click aimed at the reply box is never read as a pick.
      onThreadOpenChange={setPinThreadOpen}
      onHiddenCountChange={reportHiddenPins}
      autoOpenFeedbackId={deepLinkId}
      onDeepLinkOpened={() => {
        // `route` and nothing else — never the feedback id, which names one
        // person's submission.
        void trackFeedbackModeEvent('feedback.pin_deep_linked', {
          route,
        }).catch(() => {})
      }}
      // The popover's triage and the feedback modal's anchored submit are the
      // two callers of one counter. Passed straight through rather than
      // wrapped: a local wrapper would be a second place for the two paths to
      // drift apart.
      onTriaged={requestAnchorRefresh}
      visible={pinsVisible}
    />
  )
}
